import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Validator from 'validator';
// TODO: Lodash should no longer be used here
// eslint-disable-next-line no-restricted-imports
import { omit } from 'lodash';

import { useContacts } from '../../../contacts/contactsHooks';

import { InlineError, ShowError, ShowSuccess } from '../../../Components/Messages';

import { FormComponentProps } from '../../../common';
import { InvitationRecipientType } from '../../types';

type InvitationRecipientsFormProps = FormComponentProps<InvitationRecipientType[]> & {
  invitationRecipients: InvitationRecipientType[];
};

const InvitationRecipientsForm: React.FC<InvitationRecipientsFormProps> = ({
  title,
  invitationRecipients,
  isSaving,
  onSave,
}) => {
  const { t } = useTranslation();
  const { contacts } = useContacts();

  const [selectedItems, setSelectedItems] = useState<InvitationRecipientType[]>(
    createSelectedItems(invitationRecipients)
  );
  const [itemEmails, setItemEmails] = useState<Record<string, string | undefined>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validate = useCallback(() => {
    const errorsLocal: Record<string, string> = {};
    if (selectedItems.length === 0) {
      errorsLocal['noInvitationRecipients'] = t('enrollments.invitationRecipientsSelectOne');
    }
    // loop through selected items. If the email exists in the itemEmails object, use that value
    // otherwise, use the email from the invitationRecipient object
    // The only way an email would exist in the itemEmails object is if the user has entered it in the input field
    selectedItems.forEach(({ email, contactId }) => {
      const emailLocal = itemEmails[contactId];

      // if recipient is selected but no email is provided
      if (!emailLocal) {
        if (!email) errorsLocal[`email-${contactId}`] = t('common.emailRequired');
      } else {
        if (!Validator.isEmail(emailLocal)) {
          errorsLocal[`email-${contactId}`] = t('Invalid email');
        } else if (email !== emailLocal) {
          const existingContact = contacts.find((contact: any) => {
            return contact.email === emailLocal;
          });
          if (existingContact) {
            errors.email = t('This email is already in use by another user. Please use a different email address');
          }
        }
      }
    });
    return errorsLocal;
  }, [contacts, errors, itemEmails, selectedItems, t]);

  useEffect(() => {
    if (isSaving && onSave) {
      const errorsLocal = validate();
      if (Object.keys(errorsLocal).length > 0) {
        setErrors(errorsLocal);
        onSave?.({ errors: errorsLocal });
      } else {
        selectedItems.forEach((item) => {
          const email = itemEmails[item.contactId];
          if (email) item.email = email;
        });
        onSave?.({ data: selectedItems });
      }
    }
  }, [errors, invitationRecipients, isSaving, itemEmails, onSave, selectedItems, validate]);

  const renderInvitationRecipientItems = useCallback(() => {
    return invitationRecipients.map((invitationRecipient) => {
      const { contactId } = invitationRecipient;
      const error = errors[`email-${contactId}`];
      const isSelected = !!selectedItems.find((item) => item.contactId === contactId);

      return (
        <div className="mb-4 flex items-center space-x-4" key={contactId}>
          <input
            type="checkbox"
            checked={isSelected}
            onChange={(e) => {
              setErrors((prev) => omit(prev, 'noInvitationRecipients'));
              if (e.target.checked) {
                if (!isSelected) setSelectedItems((prev) => [...prev, invitationRecipient]);
              } else {
                if (isSelected) setSelectedItems((prev) => prev.filter((item) => item.contactId !== contactId));
              }
            }}
            className="form-checkbox h-5 w-5 text-blue-600"
            data-testid="invitation-recipient"
          />
          <div className="flex-grow" data-testid="ir-details">
            <h4 className="font-bold">{invitationRecipient.displayName}</h4>
            {invitationRecipient.email ? (
              <div>{invitationRecipient.email}</div>
            ) : (
              isSelected && (
                <div className={`mt-2 ${error ? 'text-red-600' : ''}`}>
                  <input
                    type="email"
                    required
                    name={`email-${invitationRecipient.contactId}`}
                    placeholder={t('enrollments.invitationRecipientsEmailPlaceholder')}
                    value={itemEmails[invitationRecipient.contactId] ?? ''}
                    onChange={(e) => {
                      setErrors((prev) => omit(prev, `email-${invitationRecipient.contactId}`));
                      setItemEmails((prev) => ({ ...prev, [invitationRecipient.contactId]: e.target.value }));
                    }}
                    className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 ${
                      error ? 'border-red-500' : ''
                    }`}
                    data-testid="ir-email-address"
                  />
                  {!!error && <InlineError text={error} data-testid="ir-error-msg" />}
                </div>
              )
            )}
          </div>
        </div>
      );
    });
  }, [errors, invitationRecipients, itemEmails, selectedItems, t]);

  const renderBanner = useCallback(() => {
    const bannerMessage = t('enrollments.invitationRecipientsSelectOne');
    const hasErrors = Object.keys(errors).length > 0;

    if (hasErrors) {
      return <ShowError errors={errors} />;
    } else if (selectedItems.length === 0) {
      return <ShowError icon="warning" content={bannerMessage} hideHeader={true} />;
    } else {
      return <ShowSuccess content={bannerMessage} hideHeader={true} />;
    }
  }, [errors, selectedItems, t]);

  return (
    <div className="space-y-4" data-testid="ir-form">
      <h2 className="text-2xl font-bold" data-testid="ir-form-title">
        {t(title ?? 'enrollments.invitationRecipientsFormTitle')} <span className="text-red-900">*</span>
      </h2>
      {renderBanner()}
      <div className="space-y-4" data-testid="ir-items">
        {renderInvitationRecipientItems()}
      </div>
    </div>
  );
};

function createSelectedItems(invitationRecipients: InvitationRecipientType[]): InvitationRecipientType[] {
  return invitationRecipients.filter((invitationRecipient) => !!invitationRecipient.email);
}

export default InvitationRecipientsForm;
